import React, { useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Tabs, Card, Button } from 'antd'
import {
  UserOutlined,
  CalendarOutlined,
  BankOutlined,
  SettingOutlined,
  LineChartOutlined,
} from '@ant-design/icons'
import { useStore } from '../../stores/root-store-context'
import ERPLayout from '../../components/layout/ERPLayout'
import ContactManagement from './ContactManagement'
import EventManagement from './EventManagement'
import ExternalCompanyManagement from './ExternalCompanyManagement'
import CrmReports from './reports/CrmReports'
import { GoogleSyncToast } from '../../components/crm/GoogleSyncToast'
import './crm.css'

const { TabPane } = Tabs

function CompanyCRM() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { applicationStore, crmStore } = useStore()
  const tenant = applicationStore.company?.Tenant || ''

  const { tabKey } = useParams<{ tabKey: string }>()

  // Redirect legacy lookup tabs to new settings route
  useEffect(() => {
    if (tabKey && ['categories', 'client-roles', 'contact-types', 'event-types'].includes(tabKey)) {
      navigate(`/${tenant}/crm/settings/${tabKey}`, { replace: true })
    }
  }, [tabKey, tenant, navigate])

  const [activeTab, setActiveTab] = useState<string>(
    tabKey && ['companies', 'contacts', 'events', 'reports'].includes(tabKey) ? tabKey : 'companies'
  )

  useEffect(() => {
    if (tabKey && ['companies', 'contacts', 'events', 'reports'].includes(tabKey) && tabKey !== activeTab) {
      setActiveTab(tabKey)
    }
  }, [tabKey, activeTab])

  useEffect(() => {
    crmStore.getExternalCompanies()
    crmStore.getContacts()
    crmStore.getCrmEvents()
  }, [crmStore])

  const tabLabelMap: Record<string, string> = {
    companies: 'Companies',
    contacts: 'Contacts',
    events: 'Events',
    reports: 'Reports',
  }

  const translatedTabLabel = useMemo(() => t(tabLabelMap[activeTab] ?? 'Companies'), [activeTab, t])

  const breadcrumbItems = useMemo(
    () => [
      { label: t('Home'), path: `/${tenant}` },
      { label: 'CRM', path: `/${tenant}/crm` },
      { label: translatedTabLabel, path: `/${tenant}/crm/${activeTab}` },
    ],
    [tenant, translatedTabLabel, activeTab, t],
  )

  const handleTabChange = (key: string) => {
    setActiveTab(key)
    navigate(`/${tenant}/crm/${key}`)
  }

  return (
    <ERPLayout breadcrumbTitle="CRM" breadcrumbItems={breadcrumbItems}>
      <div className="container crm-page-container" style={{ margin: '20px auto 50px' }}>
        <Card bordered={false} className="crm-main-card">
          <div className="crm-mobile-settings-row">
            <Button
              className="crm-settings-btn"
              icon={<SettingOutlined />}
              onClick={() => navigate(`/${tenant}/crm/settings`)}
            >
              {t('Settings')}
            </Button>
          </div>

          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            size="large"
            className="crm-modern-tabs"
            tabBarExtraContent={{
              right: (
                <Button
                  className="crm-settings-btn crm-desktop-settings-btn"
                  icon={<SettingOutlined />}
                  onClick={() => navigate(`/${tenant}/crm/settings`)}
                >
                  {t('Settings')}
                </Button>
              ),
            }}
          >
            <TabPane
              tab={
                <span>
                  <BankOutlined /> {t('Companies')}
                  {crmStore.externalCompanies.length > 0 && (
                    <span className="crm-tab-count">{crmStore.externalCompanies.length}</span>
                  )}
                </span>
              }
              key="companies"
            >
              <ExternalCompanyManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <UserOutlined /> {t('Contacts')}
                  {crmStore.contacts.length > 0 && (
                    <span className="crm-tab-count">{crmStore.contacts.length}</span>
                  )}
                </span>
              }
              key="contacts"
            >
              <ContactManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <CalendarOutlined /> {t('Events')}
                  {crmStore.CrmEvents.length > 0 && (
                    <span className="crm-tab-count">{crmStore.CrmEvents.length}</span>
                  )}
                </span>
              }
              key="events"
            >
              <EventManagement />
            </TabPane>

            <TabPane
              tab={
                <span>
                  <LineChartOutlined /> {t('Reports')}
                </span>
              }
              key="reports"
            >
              <CrmReports />
            </TabPane>
          </Tabs>
        </Card>
      </div>

      <GoogleSyncToast
        state={crmStore.syncToast}
        onClose={crmStore.closeSyncToast}
      />
    </ERPLayout>
  )
}

export default observer(CompanyCRM)